import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    Signal,
    effect,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { TeaserBetConfig } from '@frontend/sports/common/client-config-data-access';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService, UserLoginEvent, UserUpdateEvent } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { range } from 'lodash-es';
import { HELP_BOX_TYPE } from 'packages/sports/web/app/src/help-box/help-box-type';
import { HelpBoxService } from 'packages/sports/web/app/src/help-box/help-box.service';
import { BehaviorSubject, filter } from 'rxjs';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipState } from '../../../../core/betslip-state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BETSLIP_TYPE_PROVIDER } from '../../../../digital/services/betslip-type-provider.service';
import { selectBetslipFlattenedPicksList } from '../../../picks/selectors';
import BetslipErrorsActions from '../../../validation/actions';
import { BetslipTeaserTrackingData, TrackingEvent } from '../../models';
import { teaserBetStateSelector } from '../../selectors';
import { ITeaserBetPickState, ITeaserBetState } from '../../state';
import { TeaserBetTrackingActions } from '../../teaser-bet-tracking.actions';

@Component({
    selector: 'bs-teaser-bet-container',
    templateUrl: './teaser-bet-container.component.html',
    styleUrls: ['./teaser-bet-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: BETSLIP_TYPE_PROVIDER, useValue: new BehaviorSubject(BetslipType.Teaser).asObservable() }],
    standalone: false,
})
export class TeaserBetContainerComponent implements OnInit, OnChanges {
    @HostBinding('class') className = 'teaser-bet-container';

    private teaserBetHelpStorageKey = 'teaserBetHelpClosed';
    private teaserBetFirstLoadStorageKey = 'teaseBetFirstTimeLoad';

    teaserInfo: ITeaserBetState;
    betslipMessages: IBetslipMessages;
    hasComboPrevention = false;
    helpBoxType = HELP_BOX_TYPE;
    isHelpBoxVisible = false;

    teaseValue: number;
    teaseSteps: number[];
    teaserStepValue: number;
    teaserStepDivider: number;

    betslipType = BetslipType.Teaser;

    pickList: Signal<BetslipPick[]>;

    @Input() inColumn = false;
    @Input() betslipState: BetslipState;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() isLinear = false;
    @Output() readonly optionToggle = new EventEmitter<void>();

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public m2User: UserService,
        private store: Store<IBetslipRootState>,
        private accountBalanceService: AccountBalanceService,
        private changeRef: ChangeDetectorRef,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private localStorage: LocalStoreService,
        private helpBoxService: HelpBoxService,
        private teaserBetConfig: TeaserBetConfig,
    ) {
        this.betslipMessages = messageProvider.getMessages();
        this.pickList = store.selectSignal(selectBetslipFlattenedPicksList);
        effect(() => this.updateSlider());
    }

    ngOnChanges(): void {
        this.isHelpBoxVisible = this.helpBoxService.isHelpBoxEnabled(HELP_BOX_TYPE.TeaserBet) && !this.localStorage.get(this.teaserBetHelpStorageKey);
    }

    ngOnInit(): void {
        this.updateSlider();
        this.store
            .select(teaserBetStateSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((teaserState) => this.updatePicks(teaserState));

        this.m2User.events
            .pipe(
                filter((e) => (e instanceof UserUpdateEvent && e.diff.has('balanceProperties')) || e instanceof UserLoginEvent),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(this.validateBetslip);

        this.accountBalanceService.selectedWalletType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.validateBetslip);
        if (!this.localStorage.get(this.teaserBetFirstLoadStorageKey)) {
            const trackingData: BetslipTeaserTrackingData = {
                action: TrackingEvent.Load,
                eventDetails: 'teaser bets',
                isLinear: this.isLinear,
            };
            this.store.dispatch(TeaserBetTrackingActions.trackTeaserPicksLoad({ payload: trackingData }));
            this.localStorage.set(this.teaserBetFirstLoadStorageKey, true);
        }
    }

    onHelpBoxClose(): void {
        this.isHelpBoxVisible = false;
        this.localStorage.set(this.teaserBetHelpStorageKey, true);
        this.changeRef.markForCheck();
    }

    get picks(): { [pickId: string]: ITeaserBetPickState } {
        return this.teaserInfo.picks;
    }

    private updatePicks(teaserState: ITeaserBetState) {
        if (teaserState.selectedPoints) {
            this.teaseValue = !this.teaseSteps.some((x) => x === teaserState.selectedPoints) ? this.teaseSteps[0] : teaserState.selectedPoints;
        }

        if (this.teaseValue > 0 && teaserState.selectedPoints === 0) {
            this.teaseValue = 0;
        }
        this.teaserInfo = teaserState;
        this.updateSlider();
        this.changeRef.detectChanges();
    }

    private validateBetslip = () => this.store.dispatch(BetslipErrorsActions.validateBetslip());

    private updateSlider() {
        let teaserPickList: BetslipPick[] = [];
        if (this.teaserInfo?.picks) {
            const selectedTeaserPickIds = Object.values(this.teaserInfo.picks)
                .filter((x) => x.isSelected)
                ?.map((m) => m.id);
            teaserPickList = this.pickList().filter((pick) => selectedTeaserPickIds?.includes(pick.id.toString())) || [];
        }

        if (!teaserPickList || teaserPickList?.length < 1) {
            teaserPickList = this.pickList();
        }

        const configList = this.teaserBetConfig.pointsSliderConfig.filter((config) => teaserPickList.some((pick) => config.sportId === pick.sportId));
        if (!this.teaseValue) {
            this.teaseValue = Math.max(...configList.map((config) => config.defaultValue));
        }
        this.teaserStepValue = Math.max(...configList.map((config) => config.stepValue));
        this.teaseSteps = range(
            Math.max(...configList.map((config) => config.startValue)),
            Math.min(...configList.map((config) => config.endValue)) + this.teaserStepValue,
            this.teaserStepValue,
        );
        this.teaserStepDivider = Math.min(...configList.map((config) => config.stepDivider));
    }
}
